import { AnyAction } from 'redux';
import {
  OrderPostCompanyViewModel,
  OrderPostTicketPageViewModel,
  SingleOrderPostTicketViewModel,
} from '../ViewModels/OrderPostViewModels';
import {
  OwnerForCard,
  SetProductUpdatePayload,
} from './OrderingProductReducer';

// State speiler backend-paginering
export interface IOrderPostState {
  orderPosts: SingleOrderPostTicketViewModel[];
  totalCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  error?: string | null;
  vendors?: OrderPostCompanyViewModel[]; // alle leverandører
  selectedVendor?: OrderPostCompanyViewModel | null; // filter
  freightCost?: number; // fraktkostnad for valgt leverandør
  vendorOrderNumber: string; // leverandørens ordrenummer
}

type SetProductUnitPricePayload = {
  unitPrice: number;
  pairs: Array<{ productId: string; ownerId: string }>;
};
export const initialOrderPostState: IOrderPostState = {
  orderPosts: [],
  totalCount: 0,
  page: 1,
  pageSize: 25,
  isLoading: false,
  error: null,
  vendors: [],
  selectedVendor: null,
  freightCost: 0,
  vendorOrderNumber: '',
};

const safe_trim = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

// Actions
export type OrderPostAction =
  | {
      type: 'ORDER_POST/REMOVE_OWNER_FROM_LINE';
      payload: { productId: string; ownerId: string };
    }
  | {
      type: 'ORDER_POST/REQUEST';
      payload?: { page?: number; pageSize?: number };
    }
  | {
      type: 'ORDER_POST/SET_PRODUCT_UNIT_PRICE';
      payload: SetProductUnitPricePayload;
    }
  | {
      type: 'ORDER_POST/SET_PRODUCT_VENDOR_URL';
      payload: { productId: string; unitPrice: number; owners: OwnerForCard[] };
    }
  | {
      type: 'ORDER_POST/SET_GROUP_UNIT_PRICE';
      payload: { vendorProductUrl: string; unitPrice: number };
    }
  | {
      type: 'ORDER_POST/SET_FREIGHT_COST';
      payload: { freightCost: number };
    }
  | {
      type: 'ORDER_POST/SET_VENDOR_ORDER_NUMBER';
      payload: { vendorOrderNumber: string };
    }
  | {
      type: 'ORDER_POST/UPDATE_PRODUCT_FIELDS';
      payload: SetProductUpdatePayload;
    }
  | {
      type: 'ORDER_POST/SET_VENDORS';
      payload: { vendors: OrderPostCompanyViewModel[] };
    }
  | {
      type: 'ORDER_POST/SET_SELECTED_VENDOR';
      payload: { vendor: OrderPostCompanyViewModel | null };
    }
  | { type: 'ORDER_POST/CLEAR_ALL_ORDERED' }
  | { type: 'ORDER_POST/SUCCESS'; payload: OrderPostTicketPageViewModel }
  | { type: 'ORDER_POST/FAIL'; payload: string }
  | { type: 'ORDER_POST/SET_PAGE'; payload: number }
  | { type: 'ORDER_POST/SET_PAGE_SIZE'; payload: number }
  | { type: 'ORDER_POST/RESET' }
  | {
      type: 'ORDER_POST/UPDATE_VENDOR_URL';
      payload: { oldUrl: string; nextUrl: string };
    }
  | {
      type: 'ORDER_POST/CLEAR_VENDOR_URL_ORDERED';
      payload: { vendorProductUrl: string };
    }
  | {
      type: 'ORDER_POST/SET_OWNER_ORDERED';
      payload: { productId: string; orderedQuantity: number; ownerId: string };
    };
// Reducer
export const OrderPostReducer = (
  state: IOrderPostState = initialOrderPostState,
  action: AnyAction
): IOrderPostState => {
  const a = action as OrderPostAction;
  switch (a.type) {
    case 'ORDER_POST/UPDATE_PRODUCT_FIELDS': {
      const { unitPrice, vendorProductUrl, pairs } =
        a.payload as SetProductUpdatePayload;

      const orderPosts = (state.orderPosts ?? []).map((t) => {
        let ticketChanged = false;
        const ticketProducts = (t.ticketProducts ?? []).map((tp) => {
          let next = tp;
          let changed = false;

          // 1) Pris via URL for alle matchende linjer
          if (
            unitPrice !== undefined &&
            vendorProductUrl &&
            (tp.vendorProductUrl || '').trim() === vendorProductUrl.trim()
          ) {
            next = { ...next, unitPrice };
            changed = true;
          }

          // 2) Pairs (mengde og ev. pris uten URL)
          if (pairs?.length) {
            const match = pairs.find((p) => {
              if (p.productId !== tp.id) return false;
              const pOwner = p.ownerId ?? '';
              const lOwner = tp.owner?.id ?? '';
              if (pOwner) return pOwner === lOwner; // owner-matchet
              if (p.ticketId) return p.ticketId === t.ticketId; // fallback når ownerId tom
              return true; // bare productId
            });

            if (match) {
              if (unitPrice !== undefined && !vendorProductUrl) {
                next = { ...next, unitPrice };
                changed = true;
              }
              if (match.quantity !== undefined) {
                const maxNeed = Math.max(
                  0,
                  (tp.quantity ?? 0) - (tp.alreadyOrdered ?? 0)
                );
                const capped = Math.min(Math.max(0, match.quantity), maxNeed);
                if (capped !== (tp.orderedQuantity ?? 0)) {
                  next = { ...next, orderedQuantity: capped };
                  changed = true;
                }
              }
            }
          }

          ticketChanged = ticketChanged || changed;
          return changed ? next : tp;
        });

        return ticketChanged ? { ...t, ticketProducts } : t;
      });

      return { ...state, orderPosts };
    }

    case 'ORDER_POST/REMOVE_OWNER_FROM_LINE': {
      const { productId, ownerId } = a.payload;
      return {
        ...state,
        orderPosts: state.orderPosts.map((ticket) => ({
          ...ticket,
          ticketProducts: ticket.ticketProducts.map((product) =>
            product.id === productId && product.owner?.id === ownerId
              ? { ...product, orderedQuantity: 0 }
              : product
          ),
        })),
      };
    }
    case 'ORDER_POST/REQUEST': {
      return {
        ...state,
        isLoading: true,
        error: null,
        page: a.payload?.page ?? state.page,
        pageSize: a.payload?.pageSize ?? state.pageSize,
      };
    }
    case 'ORDER_POST/SET_GROUP_UNIT_PRICE': {
      const { vendorProductUrl, unitPrice } = a.payload as {
        vendorProductUrl: string;
        unitPrice: number;
      };
      const key = safe_trim(vendorProductUrl);

      return {
        ...state,
        orderPosts: state.orderPosts.map((t) => ({
          ...t,
          ticketProducts: t.ticketProducts.map((p) =>
            safe_trim(p.vendorProductUrl) === key ? { ...p, unitPrice } : p
          ),
        })),
      };
    }
    case 'ORDER_POST/SET_PRODUCT_UNIT_PRICE': {
      const { unitPrice, pairs } = a.payload as SetProductUnitPricePayload;
      const mk = (p: { productId: string; ownerId: string }) =>
        `${safe_trim(p.productId)}::${safe_trim(p.ownerId)}`;
      const keys = new Set(pairs.map(mk));

      const orderPosts = state.orderPosts.map((t) => {
        let changed = false;
        const ticketProducts = t.ticketProducts.map((p) => {
          const k = `${safe_trim(p.id)}::${safe_trim(p.owner?.id)}`;
          if (keys.has(k)) {
            changed = true;
            return { ...p, unitPrice };
          }
          return p;
        });
        return changed ? { ...t, ticketProducts } : t;
      });

      return { ...state, orderPosts };
    }

    case 'ORDER_POST/SET_PRODUCT_VENDOR_URL': {
      const { productId, unitPrice } = a.payload as {
        productId: string;
        unitPrice: number;
        owners: OwnerForCard[];
      };
      const pid = safe_trim(productId); // før: if (!productId.trim())
      if (!pid) return state;

      return {
        ...state,
        orderPosts: state.orderPosts.map((t) => ({
          ...t,
          ticketProducts: t.ticketProducts.map((p) =>
            safe_trim(p.id) === pid ? { ...p, unitPrice } : p
          ),
        })),
      };
    }

    case 'ORDER_POST/SET_FREIGHT_COST': {
      return {
        ...state,
        freightCost: a.payload.freightCost,
      };
    }
    case 'ORDER_POST/SET_VENDOR_ORDER_NUMBER': {
      return {
        ...state,
        vendorOrderNumber: a.payload.vendorOrderNumber,
      };
    }
    case 'ORDER_POST/SET_VENDORS': {
      console.log('Vendors:', a.payload);
      return {
        ...state,
        vendors: a.payload.vendors,
      };
    }
    case 'ORDER_POST/SET_SELECTED_VENDOR': {
      console.log('Vendors changed:', a.payload.vendor);
      return {
        ...state,
        selectedVendor: a.payload.vendor,
      };
    }
    case 'ORDER_POST/CLEAR_ALL_ORDERED': {
      return {
        ...state,
        orderPosts: state.orderPosts.map((t) => ({
          ...t,
          ticketProducts: t.ticketProducts.map((p) => ({
            ...p,
            orderedQuantity: 0,
          })),
        })),
      };
    }
    case 'ORDER_POST/SUCCESS': {
      const p = a.payload;
      return {
        ...state,
        isLoading: false,
        orderPosts: p.orderPosts,
        totalCount: p.totalCount,
        page: p.page,
        pageSize: p.pageSize,
      };
    }
    case 'ORDER_POST/FAIL':
      return {
        ...state,
        isLoading: false,
        error: a.payload,
      };

    case 'ORDER_POST/SET_PAGE':
      return { ...state, page: a.payload };

    case 'ORDER_POST/SET_PAGE_SIZE':
      // reset til side 1 når pageSize endres
      return { ...state, pageSize: a.payload, page: 1 };

    case 'ORDER_POST/RESET':
      return { ...initialOrderPostState };

    case 'ORDER_POST/UPDATE_VENDOR_URL': {
      const { oldUrl, nextUrl } = a.payload;
      const oldK = safe_trim(oldUrl);
      const newK = safe_trim(nextUrl);
      if (!oldK || !newK) return state;

      return {
        ...state,
        orderPosts: state.orderPosts.map((ticket) => ({
          ...ticket,
          ticketProducts: ticket.ticketProducts.map((p) =>
            safe_trim(p.vendorProductUrl) === oldK
              ? { ...p, vendorProductUrl: newK }
              : p
          ),
        })),
      };
    }

    case 'ORDER_POST/CLEAR_VENDOR_URL_ORDERED': {
      const key = safe_trim(a.payload.vendorProductUrl);
      if (!key) return state;

      return {
        ...state,
        orderPosts: state.orderPosts.map((ticket) => ({
          ...ticket,
          ticketProducts: ticket.ticketProducts.map((p) =>
            safe_trim(p.vendorProductUrl) === key
              ? { ...p, orderedQuantity: 0 }
              : p
          ),
        })),
      };
    }

    case 'ORDER_POST/SET_OWNER_ORDERED': {
      const { productId, orderedQuantity, ownerId } = a.payload;
      return {
        ...state,
        orderPosts: state.orderPosts.map((ticket) => ({
          ...ticket,
          ticketProducts: ticket.ticketProducts.map((p) =>
            safe_trim(p.id || '') === safe_trim(productId) &&
            safe_trim(p.owner?.id || '') === safe_trim(ownerId)
              ? { ...p, orderedQuantity }
              : p
          ),
        })),
      };
    }

    default:
      return state;
  }
};

// (valgfritt) enkle selectors
export const selectOrderPosts = (s: { orderPost: IOrderPostState }) =>
  s.orderPost.orderPosts;
export const selectOrderPaging = (s: { orderPost: IOrderPostState }) => ({
  page: s.orderPost.page,
  pageSize: s.orderPost.pageSize,
  totalCount: s.orderPost.totalCount,
});
export const selectOrderLoading = (s: { orderPost: IOrderPostState }) =>
  s.orderPost.isLoading;
export const selectOrderError = (s: { orderPost: IOrderPostState }) =>
  s.orderPost.error;
